// User Role Badges Configuration - Single Source of Truth
// Edit badges here to update them across the entire application

export const USER_ROLE_BADGES = {
  visitor: {
    text: 'VISITOR',
    className: 'bg-gray-100 text-gray-500',
    description: 'No login required, limited access'
  },
  lead_unverified_static_1: {
    text: 'LEAD',
    className: 'bg-amber-50 text-amber-600 border border-amber-200',
    description: 'Email only, can view assessment'
  },
  user_verified_static_1: {
    text: 'FREE',
    className: 'bg-gray-200 text-gray-600',
    description: 'Basic verified user'
  },
  // Tier 2 - Report (Static)
  user_paid_static_2: {
    text: 'REPORT',
    className: 'bg-blue-50 text-blue-700 border border-blue-200',
    avatarStyle: {
      backgroundColor: '#3b82f6',
      boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.15), 0 2px 4px rgba(0, 0, 0, 0.2)'
    },
    description: 'Report - Static'
  },
  // Tier 2 - Report (Dynamic)
  user_dynamic_2: {
    text: 'DYNAMIC',
    className: 'bg-blue-100 text-blue-800 border border-blue-300',
    avatarStyle: {
      backgroundColor: '#2563eb',
      boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.15), 0 2px 4px rgba(0, 0, 0, 0.2)'
    },
    description: 'Dynamic'
  },
  // Tier 3 - Studio (Static)
  user_paid_static_3: {
    text: 'DYNAMIC',
    className: 'bg-blue-700 text-white border border-blue-500',
    style: {
      fontFamily: "'Google Sans', sans-serif",
      fontWeight: '600',
      fontSize: '0.8125rem',
      padding: '0.3rem 0.7rem',
      boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.1), 0 1px 3px rgba(0, 0, 0, 0.3)',
      textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)'
    },
    avatarStyle: {
      backgroundColor: '#1d4ed8',
      boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.1), 0 2px 4px rgba(0, 0, 0, 0.3)'
    },
    description: 'Dynamic'
  },
  // Tier 3 - Studio (Dynamic)
  user_dynamic_3: {
    text: 'DYNAMIC',
    className: 'bg-blue-800 text-white border border-blue-600',
    style: {
      fontFamily: "'Google Sans', sans-serif",
      fontWeight: '600',
      fontSize: '0.8125rem',
      padding: '0.3rem 0.7rem',
      boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.1), 0 1px 3px rgba(0, 0, 0, 0.3)',
      textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)'
    },
    avatarStyle: {
      backgroundColor: '#1e40af',
      boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.1), 0 2px 4px rgba(0, 0, 0, 0.3)'
    },
    description: 'Dynamic'
  },
  user_pro: {
    text: 'DYNAMIC',
    className: 'text-white',
    style: {
      backgroundColor: '#0f172a', /* Dark slate for Lab */
      fontFamily: "'Google Sans', sans-serif",
      fontSize: '0.75rem', /* Bump up size slightly */
      fontWeight: '600',
      padding: '0.3rem 0.7rem'
    },
    description: 'ADU Lab - Full Agentic Orchestration'
  },
  admin: {
    text: 'ADMIN',
    className: 'bg-red-600 text-white',
    description: 'Full system access'
  }
};

const normalizeEntitlements = (entitlements) => new Set(
  Array.isArray(entitlements)
    ? entitlements.filter((entitlement) => typeof entitlement === 'string')
    : []
);

const normalizeSubscriptionStatus = (account) => {
  if (typeof account?.subscriptionStatus === 'string') {
    return account.subscriptionStatus.toLowerCase();
  }
  if (typeof account?.subscription_status === 'string') {
    return account.subscription_status.toLowerCase();
  }
  return '';
};

export const hasDynamicAccountTruth = (entitlements, isDynamic = false, account = {}) => {
  const entitlementSet = normalizeEntitlements(entitlements);
  const subscriptionStatus = normalizeSubscriptionStatus(account);
  return entitlementSet.has('dynamic')
    || isDynamic === true
    || account?.isDynamic === true
    || subscriptionStatus === 'active';
};

export const hasReportAccountTruth = (entitlements, account = {}) => {
  const entitlementSet = normalizeEntitlements(entitlements);
  return entitlementSet.has('report')
    || entitlementSet.has('insight')
    || account?.reportPurchaseDate != null;
};

export const shouldShowUpgradeAction = (status, roles, entitlements, isDynamic = false, account = {}) => {
  const entitlementSet = normalizeEntitlements(entitlements);
  if (roles?.includes('admin')) return false;
  if (hasDynamicAccountTruth(entitlements, isDynamic, account)) return false;
  if (hasReportAccountTruth(entitlements, account)) return status === 'active';
  if (
    entitlementSet.has('lab')
    || entitlementSet.has('pro_plus')
    || entitlementSet.has('studio')
    || entitlementSet.has('navigator')
  ) {
    return false;
  }
  return status === 'active';
};

export const getAccountPlanLabel = (status, roles, entitlements, isDynamic = false, account = {}) => {
  const badge = getBadgeFromUserData(status, roles, entitlements, isDynamic, account);
  switch (badge?.text) {
    case 'ADMIN':
      return 'Admin';
    case 'LAB':
      return 'ADU Lab';
    case 'DYNAMIC':
    case 'STUDIO':
    case 'STUDIO+':
      return 'Dynamic';
    case 'REPORT':
    case 'REPORT+':
      return 'Report';
    case 'FREE':
      return 'Free';
    case 'LEAD':
      return 'Lead';
    default:
      return null;
  }
};

// New function to get badge based on status/entitlements architecture
export const getBadgeFromUserData = (status, roles, entitlements, isDynamic = false, account = {}) => {
  // Admin always gets admin badge
  if (roles?.includes('admin')) {
    return USER_ROLE_BADGES.admin;
  }

  // Pending verification
  if (status === 'pending_verification') {
    return USER_ROLE_BADGES.lead_unverified_static_1;
  }

  // Active users - check entitlements and dynamic status
  if (status === 'active') {
    const entitlementSet = normalizeEntitlements(entitlements);

    // DLG account truth outranks stale legacy tier fields.
    if (hasDynamicAccountTruth(entitlements, isDynamic, account)) {
      return USER_ROLE_BADGES.user_dynamic_2;
    }

    if (hasReportAccountTruth(entitlements, account)) {
      return USER_ROLE_BADGES.user_paid_static_2;
    }

    // Tier 3: Lab (formerly Pro+)
    if (entitlementSet.has('lab') || entitlementSet.has('pro_plus')) {
      return USER_ROLE_BADGES.user_pro;
    }
    // Tier 2: Studio (formerly Navigator)
    if (entitlementSet.has('studio') || entitlementSet.has('navigator')) {
      // Check if dynamic for Navigator+
      return isDynamic ? USER_ROLE_BADGES.user_dynamic_3 : USER_ROLE_BADGES.user_paid_static_3;
    }
    // Tier 1: Report (formerly Insight)
    if (entitlementSet.has('insight')) {
      // Check if dynamic for Insight+
      return isDynamic ? USER_ROLE_BADGES.user_dynamic_2 : USER_ROLE_BADGES.user_paid_static_2;
    }
    // No entitlements = free user
    return USER_ROLE_BADGES.user_verified_static_1;
  }

  // Suspended or other status
  return USER_ROLE_BADGES.visitor;
};

// DEPRECATED - Old helper function, use getBadgeFromUserData instead
export const getRoleBadge = (userRole) => {
  console.warn('getRoleBadge is deprecated. Use getBadgeFromUserData with status/entitlements instead.');
  return USER_ROLE_BADGES[userRole] || null;
};

// Get all badges for UI Kit display
export const getAllBadges = () => {
  return Object.entries(USER_ROLE_BADGES).map(([role, config]) => ({
    role,
    ...config
  }));
};
